/**
 * src/components/layout/index.js — Footer and Layout
 */
import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import Navbar from './Navbar';

export function Footer() {
  const TC = [
    'Minimum order value must be ₹3,000.',
    '100% advance payment must accompany your order confirmation.',
    'Handling & packing charges are 2% extra on order value.',
    'Freight / delivery charges are additional and vary by location.',
  ];

  const TRUST_BADGES = [
    { icon: '🏭', label: 'Sivakasi Sourced' },
    { icon: '✅', label: 'Quality Certified' },
    { icon: '⚖️', label: 'SC Compliant' },
    { icon: '🔒', label: 'Safe Packaging' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-red-950 to-black text-red-100 overflow-hidden">
      {/* Top gold line */}
      <div className="h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"/>
      <div className="absolute inset-0 deco-stripes opacity-20 pointer-events-none"/>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

        {/* ── Main grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12
          border-b border-white/8">

          {/* ── Col 1: Brand Story ── */}
          <div className="sm:col-span-2 lg:col-span-1">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 mb-5 group w-fit">
              <img src="/logo.png" alt="Lakshmi Crackers"
                className="h-20 w-20 object-contain group-hover:scale-110 transition-transform duration-300"
                style={{ filter: 'drop-shadow(0 0 12px rgba(245,200,66,0.7))' }}/>
              <div>
                <p className="font-black text-xl text-yellow-300 leading-tight tracking-wide"
                  style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  LAKSHMI CRACKERS
                </p>
                <p className="text-yellow-400/60 text-[10px] font-semibold uppercase tracking-[0.2em] mt-0.5">
                  Where every spark, speaks joy!!
                </p>
              </div>
            </Link>

            {/* About text */}
            <p className="text-sm text-red-300/70 leading-relaxed mb-5">
              Born out of a love for celebration, Lakshmi Crackers has been lighting up
              Diwali for families across India. Every cracker we sell is sourced directly
              from licensed manufacturers in <span className="text-yellow-400/80 font-semibold">Sivakasi</span> —
              India's fireworks capital — ensuring quality, safety, and a price that puts
              a smile on your face.
            </p>
            <p className="text-sm text-red-300/70 leading-relaxed mb-6">
              We don't just sell crackers — we help you create memories that last a lifetime.
              Celebrate with love. Celebrate with <span className="text-yellow-400/80 font-semibold">Lakshmi Crackers</span>.
              <span className="ml-1">🎆</span>
            </p>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-2">
              {TRUST_BADGES.map(({ icon, label }) => (
                <div key={label}
                  className="flex items-center gap-2 bg-white/5 border border-white/8
                    rounded-xl px-3 py-2">
                  <span className="text-base">{icon}</span>
                  <span className="text-red-300/70 text-xs font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Col 2: Quick Links ── */}
          <div>
            <h3 className="text-yellow-400/80 font-black text-xs uppercase tracking-[0.15em] mb-5">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                ['🏠', 'Home',        '/'],
                ['🎆', 'Products',    '/products'],
                ['👤', 'About Us',    '/about'],
                ['🛡️', 'Safety',     '/safety'],
                ['📞', 'Contact',    '/contact'],
                ['🔍', 'Track Order', '/track-order'],
                ['📄', 'Price List', '/pricelist.pdf'],
              ].map(([icon, label, to]) => (
                <li key={to}>
                  {to.endsWith('.pdf') ? (
                    <a href={to} target="_blank" rel="noreferrer"
                      className="text-sm text-red-300/60 hover:text-yellow-300
                        transition-colors duration-200 flex items-center gap-2.5 group/link">
                      <span className="text-base w-5 flex-shrink-0">{icon}</span>
                      {label}
                    </a>
                  ) : (
                    <Link to={to}
                      className="text-sm text-red-300/60 hover:text-yellow-300
                        transition-colors duration-200 flex items-center gap-2.5 group/link">
                      <span className="text-base w-5 flex-shrink-0">{icon}</span>
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Categories shortcut */}
            <h3 className="text-yellow-400/80 font-black text-xs uppercase tracking-[0.15em] mt-7 mb-4">
              Shop by Category
            </h3>
            <ul className="space-y-2">
              {[
                ['💥', 'One Sound',      'one-sound'],
                ['🚀', 'Rockets',        'rockets'],
                ['✨', 'Sparklers',      'sparklers'],
                ['🎆', 'Sky Shots',      'sky-shots'],
                ['🎁', 'Gift Boxes',     'gift-boxes'],
                ['🎊', 'Combo Items',    'combo-packs'],
              ].map(([icon, label, slug]) => (
                <li key={slug}>
                  <Link to={`/products?category=${slug}`}
                    className="text-xs text-red-300/55 hover:text-yellow-300
                      transition-colors duration-200 flex items-center gap-2 group/link">
                    <span className="w-4 flex-shrink-0">{icon}</span>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Col 3: Shop Location ── */}
          <div>
            <h3 className="text-yellow-400/80 font-black text-xs uppercase tracking-[0.15em] mb-5">
              Visit Our Shop
            </h3>

            {/* Map embed */}
            <div className="rounded-2xl overflow-hidden border border-white/10 mb-4"
              style={{ height: '180px' }}>
              <iframe
                title="Lakshmi Crackers Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3937.185742894369!2d77.75476507455951!3d9.316804284418394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b06b7000b51df8d%3A0x5e165236fa55869e!2sLakshmi%20Crackers!5e0!3m2!1sen!2sin!4v1777051249195!5m2!1sen!2sin"
                width="100%"
                height="180"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Address block */}
            <div className="bg-white/5 border border-white/8 rounded-2xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-yellow-400 text-lg flex-shrink-0 mt-0.5">📍</span>
                <div>
                  <p className="text-yellow-300/90 text-xs font-black uppercase tracking-wider mb-1">
                    Shop Address
                  </p>
                  <p className="text-red-300/70 text-xs leading-relaxed">
                    4/214, Near Enammeenatchipuram Bus Stop,<br/>
                    Vembakottai, Sivakasi, Tamil Nadu — 626 131
                  </p>
                  <a
                    href="https://www.google.com/maps?q=Lakshmi+Crackers,Vembakottai,Sivakasi,Tamil+Nadu"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-yellow-400/70
                      hover:text-yellow-300 text-[11px] font-bold transition-colors">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    Get Directions
                  </a>
                </div>
              </div>
              <div className="border-t border-white/8 pt-3 flex items-center gap-3">
                <span className="text-yellow-400 text-base flex-shrink-0">⏰</span>
                <div>
                  <p className="text-red-300/70 text-xs">Mon – Sat: 9:00 AM – 8:00 PM</p>
                  <p className="text-red-300/50 text-xs">Sunday: 10:00 AM – 6:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Col 4: Contact + T&C ── */}
          <div>
            <h3 className="text-yellow-400/80 font-black text-xs uppercase tracking-[0.15em] mb-5">
              Get in Touch
            </h3>

            <ul className="space-y-3 mb-7">
              {[
                ['📞', '+91 63748 01083', 'tel:+916374801083'],
                ['✉️', 'lakshmicrackersonline@gmail.com', 'mailto:lakshmicrackersonline@gmail.com'],
                ['💬', 'WhatsApp Us', 'https://wa.me/916374801083'],
              ].map(([icon, label, href]) => (
                <li key={label}>
                  <a href={href} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2.5 text-sm text-red-300/60
                      hover:text-yellow-300 transition-colors group/link">
                    <span className="text-base w-5 flex-shrink-0">{icon}</span>
                    {label}
                  </a>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="mb-7">
              <h4 className="text-yellow-400/70 font-black text-[10px] uppercase
                tracking-[0.15em] mb-3">
                Follow Us
              </h4>
              <div className="flex items-center gap-3">

                {/* Instagram */}
                <a href="https://www.instagram.com/lakshmi__crackers"
                  target="_blank" rel="noreferrer"
                  aria-label="Follow us on Instagram"
                  className="group flex items-center justify-center w-10 h-10
                    rounded-xl bg-white/5 border border-white/10
                    hover:border-pink-400/60
                    transition-all duration-300 hover:-translate-y-0.5"
                  style={{ '--tw-hover-bg': 'transparent' }}>
                  <svg className="w-5 h-5 text-red-300/60 group-hover:text-pink-400 transition-colors"
                    fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>

                {/* YouTube */}
                <a href="https://www.youtube.com/@LakshmiCrackersSivakasi"
                  target="_blank" rel="noreferrer"
                  aria-label="Subscribe to our YouTube channel"
                  className="group flex items-center justify-center w-10 h-10
                    rounded-xl bg-white/5 border border-white/10
                    hover:border-red-500/60
                    transition-all duration-300 hover:-translate-y-0.5">
                  <svg className="w-5 h-5 text-red-300/60 group-hover:text-red-500 transition-colors"
                    fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12
                      3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0
                      3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505
                      0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502
                      -5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>

                {/* WhatsApp */}
                <a href="https://wa.me/916374801083"
                  target="_blank" rel="noreferrer"
                  aria-label="Chat on WhatsApp"
                  className="group flex items-center justify-center w-10 h-10
                    rounded-xl bg-white/5 border border-white/10
                    hover:bg-[#25D366] hover:border-[#25D366]
                    transition-all duration-300 hover:-translate-y-0.5">
                  <svg className="w-5 h-5 text-red-300/60 group-hover:text-white transition-colors"
                    fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </a>

              </div>
            </div>

            {/* T&C */}
            <div>
              <h4 className="text-yellow-400/70 font-black text-[10px] uppercase
                tracking-[0.15em] mb-3">
                Terms & Conditions
              </h4>
              <ul className="space-y-2">
                {TC.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-red-300/45
                    text-xs leading-relaxed">
                    <span className="text-yellow-600/70 font-black flex-shrink-0 mt-0.5">
                      {i + 1}.
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social proof / seasonal note */}
            <div className="mt-6 bg-yellow-400/8 border border-yellow-400/15
              rounded-2xl px-4 py-3">
              <p className="text-yellow-400/70 text-[11px] font-semibold leading-relaxed">
                🎆 Trusted by <span className="font-black text-yellow-300">500+ families</span> across
                Tamil Nadu for safe, affordable Diwali celebrations every year.
              </p>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-red-400/40">
            © {new Date().getFullYear()} Lakshmi Crackers. All rights reserved.
            Made with ❤️ in Tamil Nadu.
          </p>
          <p className="text-xs text-red-400/40 flex items-center gap-1.5">
            <span className="text-yellow-400/60">⚠️</span>
            Always celebrate safely. Follow Supreme Court guidelines.
          </p>
        </div>
      </div>
    </footer>
  );
}

export function Layout() {
  return (
    <div className="flex flex-col min-h-screen" style={{ background: '#fffaf0' }}>
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
